import { Outlet } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faWrench,
  faClipboardCheck,
  faListCheck,
  faCircleCheck,
  faBoxesStacked,
  faBuilding,
  faChartBar,
  faChartLine,
  faShield,
  faBolt,
  faUsersGear,
  faGears,
} from '@fortawesome/free-solid-svg-icons';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';

interface Feature {
  icon: IconDefinition;
  label: string;
}

const FEATURES: Feature[] = [
  { icon: faWrench, label: 'Equipment' },
  { icon: faClipboardCheck, label: 'Inspections' },
  { icon: faListCheck, label: 'Procedures' },
  { icon: faCircleCheck, label: 'Approvals' },
  { icon: faBoxesStacked, label: 'Inventory' },
  { icon: faBuilding, label: 'Customers' },
  { icon: faChartBar, label: 'Reports' },
  { icon: faChartLine, label: 'Analytics' },
];

const TRUST = [
  { icon: faShield, label: 'Secure' },
  { icon: faGears, label: 'Multi-Company' },
  { icon: faBolt, label: 'Fast' },
  { icon: faUsersGear, label: 'Built for Service Teams' },
];

export function AuthLayout() {
  return (
    <div className="flex min-h-screen">
      {/* ── Left panel (60%) — hidden on mobile ──────────────────────────── */}
      <div
        className="relative hidden lg:flex lg:w-3/5 flex-col justify-between overflow-hidden p-12 xl:p-16"
        style={{
          background:
            'linear-gradient(145deg, #141f0b 0%, #1e2d0e 30%, #283618 60%, #1e2d0e 85%, #141f0b 100%)',
        }}
      >
        {/* Dot grid overlay */}
        <div
          className="pointer-events-none absolute inset-0 opacity-[0.07]"
          style={{
            backgroundImage:
              'radial-gradient(circle, #ffffff 1.5px, transparent 1.5px)',
            backgroundSize: '36px 36px',
          }}
        />

        {/* Abstract industrial SVG — low opacity background illustration */}
        <div className="pointer-events-none absolute right-0 top-0 h-full w-full opacity-[0.04]">
          <svg
            viewBox="0 0 800 600"
            xmlns="http://www.w3.org/2000/svg"
            className="h-full w-full"
            preserveAspectRatio="xMidYMid slice"
          >
            <circle cx="650" cy="300" r="240" fill="none" stroke="#ffffff" strokeWidth="2" />
            <circle cx="650" cy="300" r="180" fill="none" stroke="#ffffff" strokeWidth="1.5" />
            <circle cx="650" cy="300" r="120" fill="none" stroke="#ffffff" strokeWidth="1" />
            <circle cx="650" cy="300" r="60" fill="none" stroke="#ffffff" strokeWidth="1" />
            <line x1="0" y1="300" x2="800" y2="300" stroke="#ffffff" strokeWidth="0.8" />
            <line x1="650" y1="0" x2="650" y2="600" stroke="#ffffff" strokeWidth="0.8" />
            <line x1="410" y1="60" x2="890" y2="540" stroke="#ffffff" strokeWidth="0.5" />
            <line x1="410" y1="540" x2="890" y2="60" stroke="#ffffff" strokeWidth="0.5" />
            <rect x="580" y="230" width="140" height="140" fill="none" stroke="#ffffff" strokeWidth="1" />
            <rect x="605" y="255" width="90" height="90" fill="none" stroke="#ffffff" strokeWidth="0.8" />
            <circle cx="150" cy="150" r="80" fill="none" stroke="#ffffff" strokeWidth="1.2" />
            <circle cx="150" cy="150" r="50" fill="none" stroke="#ffffff" strokeWidth="0.8" />
            <circle cx="150" cy="150" r="20" fill="none" stroke="#ffffff" strokeWidth="0.6" />
            <line x1="70" y1="150" x2="230" y2="150" stroke="#ffffff" strokeWidth="0.5" />
            <line x1="150" y1="70" x2="150" y2="230" stroke="#ffffff" strokeWidth="0.5" />
            <circle cx="100" cy="480" r="60" fill="none" stroke="#ffffff" strokeWidth="1" />
            <circle cx="100" cy="480" r="35" fill="none" stroke="#ffffff" strokeWidth="0.8" />
          </svg>
        </div>

        {/* ── Content ──────────────────────────────────────────────────────── */}
        <div className="relative z-10 flex flex-col gap-10">
          {/* Logo */}
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/10 ring-1 ring-white/20">
              <FontAwesomeIcon icon={faWrench} className="h-5 w-5 text-white" />
            </div>
            <span className="text-base font-semibold tracking-wide text-white">
              Service Portal
            </span>
          </div>

          {/* Headline */}
          <div>
            <h1 className="text-3xl font-semibold leading-snug text-white xl:text-4xl">
              Everything you need to manage your service operations in one place.
            </h1>
            <p className="mt-4 text-sm leading-relaxed text-white/60">
              Service Portal helps field service teams stay organized, move faster, and deliver
              better outcomes — from equipment intake to inspection sign-off.
            </p>
          </div>

          {/* Feature cards */}
          <div className="grid grid-cols-4 gap-3">
            {FEATURES.map((f) => (
              <div
                key={f.label}
                className="flex flex-col items-center gap-2.5 rounded-xl border border-white/10 bg-white/[0.06] px-3 py-4 transition-colors hover:bg-white/[0.10]"
              >
                <FontAwesomeIcon
                  icon={f.icon}
                  className="h-5 w-5"
                  style={{ color: '#dda15e' }}
                />
                <span className="text-xs font-medium text-white/75">{f.label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Trust bar */}
        <div className="relative z-10 flex flex-wrap items-center gap-6 border-t border-white/10 pt-6">
          {TRUST.map((t) => (
            <div key={t.label} className="flex items-center gap-2">
              <FontAwesomeIcon
                icon={t.icon}
                className="h-3.5 w-3.5"
                style={{ color: '#606c38' }}
              />
              <span className="text-xs text-white/50">{t.label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* ── Right panel (40%) — full width on mobile ──────────────────────── */}
      <div className="flex flex-1 flex-col items-center justify-center bg-background px-6 py-10 lg:w-2/5">
        <div className="w-full max-w-[360px]">
          <Outlet />
        </div>
      </div>
    </div>
  );
}
